let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  Modal,
  ActivityIndicator,
  RefreshControl,
  Share,
  Animated,
  FlatList,
  Image
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// Mock AssessmentResultsIntegrationService since it's missing
const AssessmentResultsIntegrationService = {
  initialize: async () => {
    console.log('AssessmentResultsIntegrationService initialized');
    return true;
  },
  
  getAllAssessmentResults: async (filters = {}) => {
    try {
      const cachedResults = await AsyncStorage.getItem(`professional_${_profUserId}_assessment_results`);
      if (cachedResults) {
        return JSON.parse(cachedResults);
      }
      
      // Return sample data if no cached results
      return [
        {
          id: 'result_1',
          assessmentType: 'technical',
          assessmentTitle: 'Technical Assessment',
          score: 85,
          passed: true,
          correctAnswers: 8,
          totalQuestions: 10,
          timeSpent: 35,
          timestamp: new Date().toISOString(),
          certificateEligible: true,
          detailedResults: []
        },
        {
          id: 'result_2',
          assessmentType: 'psychometric',
          assessmentTitle: 'Psychometric Test',
          score: 78,
          passed: true,
          correctAnswers: 15,
          totalQuestions: 20,
          timeSpent: 25,
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          certificateEligible: true,
          detailedResults: []
        }
      ];
    } catch (error) {
      console.error('Error getting assessment results:', error);
      return [];
    }
  },
  
  getAssessmentAnalytics: async () => {
    return {
      overview: {
        totalAssessments: 15,
        averageScore: 82,
        passedAssessments: 12,
        failedAssessments: 3,
        totalTimeSpent: 450,
        improvementRate: 15.5
      },
      byType: {
        technical: { count: 8, averageScore: 85, passRate: 87.5 },
        psychometric: { count: 4, averageScore: 78, passRate: 75 },
        ai_interview: { count: 2, averageScore: 88, passRate: 100 },
        ar_portfolio: { count: 1, averageScore: 92, passRate: 100 }
      },
      trends: {
        scoreProgression: [65, 72, 78, 82, 85],
        monthlyCompletion: [3, 5, 4, 3],
        skillImprovement: {
          technical: 12,
          communication: 8,
          problemSolving: 15
        }
      },
      certificates: [
        {
          id: 'cert_1',
          title: 'Technical Excellence Certificate',
          issueDate: new Date().toISOString(),
          validUntil: new Date(Date.now() + 31536000000).toISOString(),
          credentialId: 'TECH-2025-001',
          downloadUrl: 'https://example.com/certificate/1'
        }
      ],
      achievements: [
        {
          id: 'ach_1',
          title: 'First Assessment Completed',
          description: 'Completed your first assessment',
          icon: 'trophy',
          unlockedDate: new Date().toISOString(),
          rarity: 'common'
        },
        {
          id: 'ach_2',
          title: 'High Scorer',
          description: 'Achieved 90+ score in an assessment',
          icon: 'star',
          unlockedDate: new Date().toISOString(),
          rarity: 'rare'
        }
      ]
    };
  },
  
  getPerformanceInsights: async () => {
    return {
      performance: {
        trend: 'Improving',
        trendPercentage: 15.5,
        strongestAreas: ['Problem Solving', 'Technical Skills'],
        improvementAreas: ['Communication', 'Time Management'],
        recommendations: [
          'Focus on communication skills development',
          'Practice time management techniques',
          'Continue building on technical strengths'
        ]
      },
      skills: {
        strongest: [
          { category: 'Technical Skills', score: 92, improvement: 8 },
          { category: 'Problem Solving', score: 88, improvement: 12 }
        ],
        needsImprovement: [
          { category: 'Communication', score: 65, improvement: -2 },
          { category: 'Time Management', score: 70, improvement: 3 }
        ]
      },
      predictions: {
        nextAssessmentScore: 87,
        skillGrowthRate: 'Above Average',
        timeToMastery: '3-4 months'
      }
    };
  },
  
  shareResults: async (resultId, options = {}) => {
    return {
      message: `Check out my assessment results! I scored 85% on my Technical Assessment.`,
      url: `https://jobeng.app/results/${resultId}`,
      title: 'Assessment Results - JobEng'
    };
  },
  
  generateCertificate: async (resultId) => {
    return {
      id: `cert_${Date.now()}`,
      title: 'Assessment Completion Certificate',
      issueDate: new Date().toISOString(),
      validUntil: new Date(Date.now() + 31536000000).toISOString(),
      credentialId: `CERT-${Date.now()}`,
      downloadUrl: 'https://example.com/certificate/new'
    };
  },
  
  deleteAssessmentResult: async (resultId) => {
    try {
      const cachedResults = await AsyncStorage.getItem(`professional_${_profUserId}_assessment_results`);
      if (cachedResults) {
        const results = JSON.parse(cachedResults);
        const filteredResults = results.filter(r => r.id !== resultId);
        await AsyncStorage.setItem(`professional_${_profUserId}_assessment_results`, JSON.stringify(filteredResults));
      }
      return true;
    } catch (error) {
      console.error('Error deleting result:', error);
      return false;
    }
  },
  
  exportResults: async (format, filters = {}) => {
    const results = await this.getAllAssessmentResults(filters);
    
    if (format === 'json') {
      return JSON.stringify(results, null, 2);
    } else if (format === 'csv') {
      const headers = 'Type,Score,Passed,Date\n';
      const rows = results.map(r => 
        `${r.assessmentType},${r.score},${r.passed},${new Date(r.timestamp).toLocaleDateString()}`
      ).join('\n');
      return headers + rows;
    } else if (format === 'pdf') {
      return { message: 'PDF export functionality would be implemented here' };
    }
    
    return results;
  },
  
  addEventListener: (event, callback) => {
    // Mock event listener
    console.log(`Event listener added for: ${event}`);
  },
  
  removeEventListener: (event, callback) => {
    // Mock event listener removal
    console.log(`Event listener removed for: ${event}`);
  }
};

const AssessmentResultsScreen = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_assessmentresultsscreen`;
    apiFetch('/api/data/assessments?sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // State management
  const [results, setResults] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [filters, setFilters] = useState({});
  
  // Animation refs
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ marginLeft: 15 }}>
          <Ionicons name="arrow-back" size={24} color="white" />
        </TouchableOpacity>
      ),
    });
  }, [navigation]);

  useEffect(() => {
    initializeScreen();
    startAnimations();
    
    // Setup event listeners
    AssessmentResultsIntegrationService.addEventListener('results_saved', handleResultsUpdated);
    AssessmentResultsIntegrationService.addEventListener('certificate_generated', handleCertificateGenerated);
    
    return () => {
      AssessmentResultsIntegrationService.removeEventListener('results_saved', handleResultsUpdated);
      AssessmentResultsIntegrationService.removeEventListener('certificate_generated', handleCertificateGenerated);
    };
  }, []);

  useEffect(() => {
    // Handle navigation from TestsInterviewsScreen
    if (route.params?.results) {
      setSelectedResult(route.params.results);
      setShowDetailModal(true);
    }
  }, [route.params]);

  const initializeScreen = async () => {
    try {
      setLoading(true);
      
      // Initialize the integration service
      await AssessmentResultsIntegrationService.initialize();
      
      // Load all data in parallel
      await Promise.all([
        loadAssessmentResults(),
        loadAnalytics(),
        loadInsights(),
        loadCertificates(),
        loadAchievements()
      ]);
      
    } catch (error) {
      console.error('Error initializing AssessmentResultsScreen:', error);
      Alert.alert('Error', 'Failed to load assessment data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      })
    ]).start();
  };

  const loadAssessmentResults = async () => {
    try {
      const allResults = await AssessmentResultsIntegrationService.getAllAssessmentResults(filters);
      setResults(allResults);
    } catch (error) {
      console.error('Error loading assessment results:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await AssessmentResultsIntegrationService.getAssessmentAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadInsights = async () => {
    try {
      const insightsData = await AssessmentResultsIntegrationService.getPerformanceInsights();
      setInsights(insightsData);
    } catch (error) {
      console.error('Error getting performance insights:', error);
    }
  };

  const loadCertificates = async () => {
    try {
      if (analytics?.certificates) {
        setCertificates(analytics.certificates);
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
    }
  };

  const loadAchievements = async () => {
    try {
      if (analytics?.achievements) {
        setAchievements(analytics.achievements);
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadAssessmentResults(),
      loadAnalytics(),
      loadInsights()
    ]);
    setRefreshing(false);
  };

  const handleResultsUpdated = (newResult) => {
    setResults(prev => [newResult, ...prev]);
    loadAnalytics(); // Refresh analytics
  };

  const handleCertificateGenerated = (certificate) => {
    setCertificates(prev => [...prev, certificate]);
    Alert.alert('Certificate Generated!', 'Your certificate is now available for download.');
  };

  const handleResultPress = (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
  };

  const handleShareResult = async (result) => {
    try {
      const shareData = await AssessmentResultsIntegrationService.shareResults(result.id, {
        includeDetails: true,
        includeFullData: false
      });
      
      await Share.share({
        message: shareData.message,
        url: shareData.url,
        title: shareData.title
      });
    } catch (error) {
      console.error('Error sharing result:', error);
      Alert.alert('Error', 'Failed to share result. Please try again.');
    }
  };

  const handleGenerateCertificate = async (result) => {
    try {
      if (!result.passed || !result.certificateEligible) {
        Alert.alert('Not Eligible', 'This assessment result is not eligible for a certificate.');
        return;
      }
      
      const certificate = await AssessmentResultsIntegrationService.generateCertificate(result.id);
      setCertificates(prev => [...prev, certificate]);
      setShowCertificateModal(true);
      
      Alert.alert('Success!', 'Certificate generated successfully!');
    } catch (error) {
      console.error('Error generating certificate:', error);
      Alert.alert('Error', 'Failed to generate certificate. Please try again.');
    }
  };

  const handleDeleteResult = async (result) => {
    Alert.alert(
      'Delete Result',
      'Are you sure you want to delete this assessment result? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await AssessmentResultsIntegrationService.deleteAssessmentResult(result.id);
              if (success) {
                setResults(prev => prev.filter(r => r.id !== result.id));
                setShowDetailModal(false);
                Alert.alert('Success', 'Assessment result deleted successfully.');
              }
            } catch (error) {
              console.error('Error deleting result:', error);
              Alert.alert('Error', 'Failed to delete result. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleExportResults = async () => {
    try {
      Alert.alert(
        'Export Results',
        'Choose export format:',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'JSON',
            onPress: async () => {
              const exportData = await AssessmentResultsIntegrationService.exportResults('json', filters);
              console.log('JSON Export:', exportData);
              Alert.alert('Export Complete', 'Results exported as JSON format.');
            }
          },
          {
            text: 'CSV',
            onPress: async () => {
              const exportData = await AssessmentResultsIntegrationService.exportResults('csv', filters);
              console.log('CSV Export:', exportData);
              Alert.alert('Export Complete', 'Results exported as CSV format.');
            }
          },
          {
            text: 'PDF Report',
            onPress: async () => {
              const exportData = await AssessmentResultsIntegrationService.exportResults('pdf', filters);
              console.log('PDF Export:', exportData);
              Alert.alert('Export Complete', 'Results exported as PDF report.');
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error exporting results:', error);
      Alert.alert('Error', 'Failed to export results. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#4CAF50';
    if (score >= 80) return '#8BC34A';
    if (score >= 70) return '#FFC107';
    if (score >= 60) return '#FF9800';
    return '#F44336';
  };

  const getAssessmentIcon = (type) => {
    const icons = {
      'technical': 'code-slash',
      'psychometric': 'school-outline',
      'ai_interview': 'videocam',
      'ar_portfolio': 'cube',
      'aptitude': 'calculator',
      'situational': 'people'
    };
    return icons[type] || 'document-text';
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#0e4e96', '#1b6ec2']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={28} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Assessment Results</Text>
        <TouchableOpacity onPress={handleExportResults} style={styles.exportHeaderButton}>
          <Ionicons name="download-outline" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[
          { id: 'overview', title: 'Overview', icon: 'grid' },
          { id: 'results', title: 'All Results', icon: 'list' },
          { id: 'analytics', title: 'Analytics', icon: 'analytics' },
          { id: 'insights', title: 'Insights', icon: 'bulb' },
          { id: 'certificates', title: 'Certificates', icon: 'ribbon' },
          { id: 'achievements', title: 'Achievements', icon: 'trophy' }
        ].map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id)}
          >
            <Ionicons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.id ? '#FFFFFF' : '#666666'} 
            />
            <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOverviewTab = () => (
    <Animated.View style={[styles.tabContent, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
      {/* Performance Summary */}
      <View style={styles.summaryContainer}>
        <LinearGradient colors={['#0e4e96', '#1565C0']} style={styles.summaryGradient}>
          <Text style={styles.summaryTitle}>Performance Summary</Text>
          <View style={styles.summaryStats}>
            <View style={styles.summaryStatItem}>
              <Text style={styles.summaryStatNumber}>{analytics?.overview?.totalAssessments || 0}</Text>
              <Text style={styles.summaryStatLabel}>Total Assessments</Text>
            </View>
            <View style={styles.summaryStatItem}>
              <Text style={styles.summaryStatNumber}>{analytics?.overview?.averageScore || 0}%</Text>
              <Text style={styles.summaryStatLabel}>Average Score</Text>
            </View>
            <View style={styles.summaryStatItem}>
              <Text style={styles.summaryStatNumber}>{analytics?.overview?.passedAssessments || 0}</Text>
              <Text style={styles.summaryStatLabel}>Passed</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* Recent Results */}
      <View style={styles.recentResultsContainer}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Recent Results</Text>
          <TouchableOpacity onPress={() => setActiveTab('results')}>
            <Text style={styles.viewAllText}>View All</Text>
          </TouchableOpacity>
        </View>
        
        {results.slice(0, 3).map((result, index) => (
          <TouchableOpacity
            key={result.id || index}
            style={styles.resultCard}
            onPress={() => handleResultPress(result)}
          >
            <View style={styles.resultHeader}>
              <View style={styles.resultIconContainer}>
                <Ionicons 
                  name={getAssessmentIcon(result.assessmentType)} 
                  size={24} 
                  color="#0e4e96" 
                />
              </View>
              <View style={styles.resultInfo}>
                <Text style={styles.resultTitle}>{result.assessmentTitle}</Text>
                <Text style={styles.resultDate}>
                  {new Date(result.timestamp).toLocaleDateString()}
                </Text>
              </View>
              <View style={styles.resultScore}>
                <Text style={[styles.scoreText, { color: getScoreColor(result.score) }]}>
                  {result.score}%
                </Text>
                <Ionicons 
                  name={result.passed ? "checkmark-circle" : "close-circle"} 
                  size={20} 
                  color={result.passed ? "#4CAF50" : "#F44336"} 
                />
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsContainer}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={() => navigation.navigate('TestsInterviews')}
          >
            <LinearGradient colors={['#4CAF50', '#66BB6A']} style={styles.quickActionGradient}>
              <Ionicons name="add-circle" size={32} color="#FFFFFF" />
              <Text style={styles.quickActionText}>Take New Assessment</Text>
            </LinearGradient>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={handleExportResults}
          >
            <LinearGradient colors={['#FF9800', '#FFB74D']} style={styles.quickActionGradient}>
              <Ionicons name="download" size={32} color="#FFFFFF" />
              <Text style={styles.quickActionText}>Export Results</Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </View>

      {/* Performance Insights Preview */}
      {insights && (
        <View style={styles.insightsPreviewContainer}>
          <Text style={styles.sectionTitle}>Performance Insights</Text>
          <View style={styles.insightCard}>
            <Text style={styles.insightTitle}>Current Trend</Text>
            <Text style={styles.insightValue}>{insights.performance?.trend || 'Stable'}</Text>
          </View>
          <View style={styles.insightCard}>
            <Text style={styles.insightTitle}>Strongest Skill</Text>
            <Text style={styles.insightValue}>
              {insights.skills?.strongest?.[0]?.category || 'N/A'}
            </Text>
          </View>
        </View>
      )}
    </Animated.View>
  );

  const renderResultsTab = () => (
    <ScrollView style={styles.tabContent}>
      <View style={styles.resultsHeader}>
        <Text style={styles.sectionTitle}>All Assessment Results</Text>
        <TouchableOpacity onPress={handleExportResults} style={styles.exportButton}>
          <Ionicons name="download" size={20} color="#0e4e96" />
          <Text style={styles.exportButtonText}>Export</Text>
        </TouchableOpacity>
      </View>
      
      {results.map((result, index) => (
        <TouchableOpacity
          key={result.id || index}
          style={styles.fullResultCard}
          onPress={() => handleResultPress(result)}
        >
          <View style={styles.fullResultHeader}>
            <View style={styles.fullResultIconContainer}>
              <Ionicons 
                name={getAssessmentIcon(result.assessmentType)} 
                size={28} 
                color="#FFFFFF" 
              />
            </View>
            <View style={styles.fullResultInfo}>
              <Text style={styles.fullResultTitle}>{result.assessmentTitle}</Text>
              <Text style={styles.fullResultSubtitle}>
                {result.assessmentType.charAt(0).toUpperCase() + result.assessmentType.slice(1)} Assessment
              </Text>
              <Text style={styles.fullResultDate}>
                {new Date(result.timestamp).toLocaleDateString()}
              </Text>
            </View>
            <View style={styles.fullResultScore}>
              <Text style={[styles.fullScoreText, { color: getScoreColor(result.score) }]}>
                {result.score}%
              </Text>
              <Text style={styles.fullResultStatus}>
                {result.passed ? 'PASSED' : 'FAILED'}
              </Text>
            </View>
          </View>
          
          <View style={styles.fullResultDetails}>
            <View style={styles.fullResultDetailItem}>
              <Text style={styles.fullResultDetailLabel}>Questions</Text>
              <Text style={styles.fullResultDetailValue}>
                {result.correctAnswers}/{result.totalQuestions}
              </Text>
            </View>
            <View style={styles.fullResultDetailItem}>
              <Text style={styles.fullResultDetailLabel}>Time</Text>
              <Text style={styles.fullResultDetailValue}>
                {formatTime(result.timeSpent * 60)}
              </Text>
            </View>
            <View style={styles.fullResultDetailItem}>
              <Text style={styles.fullResultDetailLabel}>Certificate</Text>
              <Text style={styles.fullResultDetailValue}>
                {result.certificateEligible ? 'Available' : 'N/A'}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderAnalyticsTab = () => (
    <ScrollView style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Performance Analytics</Text>
      
      {analytics && (
        <>
          {/* Overview Stats */}
          <View style={styles.analyticsGrid}>
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsValue}>{analytics.overview.totalAssessments}</Text>
              <Text style={styles.analyticsLabel}>Total Assessments</Text>
            </View>
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsValue}>{analytics.overview.averageScore}%</Text>
              <Text style={styles.analyticsLabel}>Average Score</Text>
            </View>
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsValue}>{analytics.overview.passedAssessments}</Text>
              <Text style={styles.analyticsLabel}>Passed</Text>
            </View>
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsValue}>{formatTime(analytics.overview.totalTimeSpent * 60)}</Text>
              <Text style={styles.analyticsLabel}>Total Time</Text>
            </View>
          </View>

          {/* By Assessment Type */}
          <View style={styles.byTypeContainer}>
            <Text style={styles.subSectionTitle}>Performance by Type</Text>
            {Object.entries(analytics.byType).map(([type, data]) => (
              <View key={type} style={styles.typeCard}>
                <View style={styles.typeHeader}>
                  <Ionicons name={getAssessmentIcon(type)} size={24} color="#0e4e96" />
                  <Text style={styles.typeTitle}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Text>
                </View>
                <View style={styles.typeStats}>
                  <Text style={styles.typeStat}>Count: {data.count}</Text>
                  <Text style={styles.typeStat}>Avg: {data.averageScore}%</Text>
                  <Text style={styles.typeStat}>Pass Rate: {data.passRate}%</Text>
                </View>
              </View>
            ))}
          </View>
        </>
      )}
    </ScrollView>
  );

  const renderInsightsTab = () => (
    <ScrollView style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Performance Insights</Text>
      
      {insights && (
        <>
          {/* Performance Trend */}
          <View style={styles.insightSection}>
            <Text style={styles.insightSectionTitle}>Performance Trend</Text>
            <View style={styles.trendCard}>
              <Text style={styles.trendValue}>{insights.performance.trend}</Text>
              <Text style={styles.trendPercentage}>
                {insights.performance.trendPercentage > 0 ? '+' : ''}
                {insights.performance.trendPercentage}%
              </Text>
            </View>
          </View>

          {/* Strongest Areas */}
          <View style={styles.insightSection}>
            <Text style={styles.insightSectionTitle}>Strongest Areas</Text>
            {insights.skills.strongest.map((skill, index) => (
              <View key={index} style={styles.skillCard}>
                <Text style={styles.skillName}>{skill.category}</Text>
                <Text style={styles.skillScore}>{skill.score}%</Text>
                <Text style={styles.skillImprovement}>
                  +{skill.improvement}% improvement
                </Text>
              </View>
            ))}
          </View>

          {/* Areas for Improvement */}
          <View style={styles.insightSection}>
            <Text style={styles.insightSectionTitle}>Areas for Improvement</Text>
            {insights.skills.needsImprovement.map((skill, index) => (
              <View key={index} style={styles.skillCard}>
                <Text style={styles.skillName}>{skill.category}</Text>
                <Text style={styles.skillScore}>{skill.score}%</Text>
                <Text style={styles.skillImprovement}>
                  {skill.improvement > 0 ? '+' : ''}{skill.improvement}% change
                </Text>
              </View>
            ))}
          </View>

          {/* Recommendations */}
          <View style={styles.insightSection}>
            <Text style={styles.insightSectionTitle}>Recommendations</Text>
            {insights.performance.recommendations.map((rec, index) => (
              <View key={index} style={styles.recommendationCard}>
                <Ionicons name="bulb-outline" size={20} color="#FF9800" />
                <Text style={styles.recommendationText}>{rec}</Text>
              </View>
            ))}
          </View>
        </>
      )}
    </ScrollView>
  );

  const renderCertificatesTab = () => (
    <ScrollView style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Your Certificates</Text>
      
      {certificates.map((cert, index) => (
        <View key={cert.id || index} style={styles.certificateCard}>
          <View style={styles.certificateHeader}>
            <Ionicons name="ribbon" size={32} color="#FFD700" />
            <View style={styles.certificateInfo}>
              <Text style={styles.certificateTitle}>{cert.title}</Text>
              <Text style={styles.certificateId}>ID: {cert.credentialId}</Text>
              <Text style={styles.certificateDate}>
                Issued: {new Date(cert.issueDate).toLocaleDateString()}
              </Text>
            </View>
          </View>
          <TouchableOpacity style={styles.downloadButton}>
            <Ionicons name="download" size={20} color="#FFFFFF" />
            <Text style={styles.downloadButtonText}>Download</Text>
          </TouchableOpacity>
        </View>
      ))}
      
      {certificates.length === 0 && (
        <View style={styles.emptyState}>
          <Ionicons name="ribbon-outline" size={64} color="#CCCCCC" />
          <Text style={styles.emptyStateText}>No certificates yet</Text>
          <Text style={styles.emptyStateSubtext}>
            Complete assessments with passing scores to earn certificates
          </Text>
        </View>
      )}
    </ScrollView>
  );

  const renderAchievementsTab = () => (
    <ScrollView style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Your Achievements</Text>
      
      {achievements.map((achievement, index) => (
        <View key={achievement.id || index} style={styles.achievementCard}>
          <View style={styles.achievementIcon}>
            <Ionicons name={achievement.icon} size={32} color="#FFD700" />
          </View>
          <View style={styles.achievementInfo}>
            <Text style={styles.achievementTitle}>{achievement.title}</Text>
            <Text style={styles.achievementDescription}>{achievement.description}</Text>
            <Text style={styles.achievementDate}>
              Unlocked: {new Date(achievement.unlockedDate).toLocaleDateString()}
            </Text>
          </View>
          <View style={styles.achievementRarity}>
            <Text style={[styles.rarityText, { color: achievement.rarity === 'rare' ? '#FF9800' : '#4CAF50' }]}>
              {String(achievement.rarity ?? '').toUpperCase()}
            </Text>
          </View>
        </View>
      ))}
      
      {achievements.length === 0 && (
        <View style={styles.emptyState}>
          <Ionicons name="trophy-outline" size={64} color="#CCCCCC" />
          <Text style={styles.emptyStateText}>No achievements yet</Text>
          <Text style={styles.emptyStateSubtext}>
            Complete assessments and reach milestones to unlock achievements
          </Text>
        </View>
      )}
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'results':
        return renderResultsTab();
      case 'analytics':
        return renderAnalyticsTab();
      case 'insights':
        return renderInsightsTab();
      case 'certificates':
        return renderCertificatesTab();
      case 'achievements':
        return renderAchievementsTab();
      default:
        return renderOverviewTab();
    }
  };

  // Result Detail Modal
  const renderResultDetailModal = () => (
    <Modal
      visible={showDetailModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowDetailModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowDetailModal(false)}>
            <Ionicons name="close" size={28} color="#333" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Assessment Details</Text>
          <TouchableOpacity onPress={() => handleShareResult(selectedResult)}>
            <Ionicons name="share-outline" size={28} color="#0e4e96" />
          </TouchableOpacity>
        </View>
        
        {selectedResult && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.detailHeader}>
              <Ionicons 
                name={getAssessmentIcon(selectedResult.assessmentType)} 
                size={48} 
                color="#0e4e96" 
              />
              <Text style={styles.detailTitle}>{selectedResult.assessmentTitle}</Text>
              <Text style={styles.detailSubtitle}>
                {selectedResult.assessmentType.charAt(0).toUpperCase() + selectedResult.assessmentType.slice(1)} Assessment
              </Text>
            </View>
            
            <View style={styles.scoreSection}>
              <Text style={[styles.bigScore, { color: getScoreColor(selectedResult.score) }]}>
                {selectedResult.score}%
              </Text>
              <Text style={styles.scoreStatus}>
                {selectedResult.passed ? 'PASSED' : 'FAILED'}
              </Text>
            </View>
            
            <View style={styles.detailStats}>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Questions Correct</Text>
                <Text style={styles.detailStatValue}>
                  {selectedResult.correctAnswers}/{selectedResult.totalQuestions}
                </Text>
              </View>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Time Spent</Text>
                <Text style={styles.detailStatValue}>
                  {formatTime(selectedResult.timeSpent * 60)}
                </Text>
              </View>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Date Completed</Text>
                <Text style={styles.detailStatValue}>
                  {new Date(selectedResult.timestamp).toLocaleDateString()}
                </Text>
              </View>
            </View>
            
            <View style={styles.actionButtons}>
              {selectedResult.certificateEligible && selectedResult.passed && (
                <TouchableOpacity 
                  style={styles.certificateButton}
                  onPress={() => handleGenerateCertificate(selectedResult)}
                >
                  <Ionicons name="ribbon" size={20} color="#FFFFFF" />
                  <Text style={styles.certificateButtonText}>Generate Certificate</Text>
                </TouchableOpacity>
              )}
              
              <TouchableOpacity 
                style={styles.shareButton}
                onPress={() => handleShareResult(selectedResult)}
              >
                <Ionicons name="share-outline" size={20} color="#FFFFFF" />
                <Text style={styles.shareButtonText}>Share Result</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.deleteButton}
                onPress={() => handleDeleteResult(selectedResult)}
              >
                <Ionicons name="trash-outline" size={20} color="#FFFFFF" />
                <Text style={styles.deleteButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading assessment results...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}
      {renderTabBar()}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {renderContent()}
      </ScrollView>
      {renderResultDetailModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  header: {
    paddingTop: height * 0.05,
    paddingBottom: 15,
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  exportHeaderButton: {
    padding: 5,
  },
  tabBar: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginHorizontal: 5,
    borderRadius: 20,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    marginLeft: 5,
    fontSize: 14,
    fontWeight: '600',
    color: '#666666',
  },
  activeTabText: {
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  tabContent: {
    padding: 20,
  },
  summaryContainer: {
    marginBottom: 20,
  },
  summaryGradient: {
    borderRadius: 15,
    padding: 20,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 15,
  },
  summaryStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  summaryStatItem: {
    alignItems: 'center',
  },
  summaryStatNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  summaryStatLabel: {
    fontSize: 12,
    color: '#E3F2FD',
    marginTop: 5,
  },
  recentResultsContainer: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  viewAllText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '600',
  },
  resultCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resultIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
  },
  resultInfo: {
    flex: 1,
    marginLeft: 15,
  },
  resultTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  resultDate: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  resultScore: {
    alignItems: 'center',
  },
  scoreText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  quickActionsContainer: {
    marginBottom: 20,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  quickActionCard: {
    flex: 1,
    marginHorizontal: 5,
  },
  quickActionGradient: {
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  quickActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 10,
    textAlign: 'center',
  },
  insightsPreviewContainer: {
    marginBottom: 20,
  },
  insightCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  insightTitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  insightValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
  },
  exportButtonText: {
    marginLeft: 5,
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '600',
  },
  fullResultCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  fullResultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  fullResultIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullResultInfo: {
    flex: 1,
    marginLeft: 15,
  },
  fullResultTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  fullResultSubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  fullResultDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
  },
  fullResultScore: {
    alignItems: 'center',
  },
  fullScoreText: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  fullResultStatus: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginTop: 2,
  },
  fullResultDetails: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  fullResultDetailItem: {
    alignItems: 'center',
  },
  fullResultDetailLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 5,
  },
  fullResultDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  analyticsCard: {
    width: '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    alignItems: 'center',
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  analyticsValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 5,
    textAlign: 'center',
  },
  byTypeContainer: {
    marginBottom: 20,
  },
  subSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  typeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  typeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  typeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginLeft: 10,
  },
  typeStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  typeStat: {
    fontSize: 14,
    color: '#666',
  },
  insightSection: {
    marginBottom: 25,
  },
  insightSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  trendCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  trendValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  trendPercentage: {
    fontSize: 16,
    color: '#666',
    marginTop: 5,
  },
  skillCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  skillName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  skillScore: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginRight: 10,
  },
  skillImprovement: {
    fontSize: 12,
    color: '#4CAF50',
  },
  recommendationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  recommendationText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 10,
    flex: 1,
  },
  certificateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  certificateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  certificateInfo: {
    flex: 1,
    marginLeft: 15,
  },
  certificateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  certificateId: {
    fontSize: 14,
    color: '#666',
    marginTop: 5,
  },
  certificateDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  downloadButtonText: {
    marginLeft: 5,
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  achievementCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 20,
    marginBottom: 15,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  achievementIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#FFF3E0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  achievementInfo: {
    flex: 1,
    marginLeft: 15,
  },
  achievementTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  achievementDescription: {
    fontSize: 14,
    color: '#666',
    marginTop: 5,
  },
  achievementDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
  },
  achievementRarity: {
    alignItems: 'center',
  },
  rarityText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 50,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#999',
    marginTop: 15,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#CCCCCC',
    marginTop: 10,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  detailHeader: {
    alignItems: 'center',
    marginBottom: 30,
  },
  detailTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 15,
  },
  detailSubtitle: {
    fontSize: 16,
    color: '#666',
    marginTop: 5,
  },
  scoreSection: {
    alignItems: 'center',
    marginBottom: 30,
  },
  bigScore: {
    fontSize: 48,
    fontWeight: 'bold',
  },
  scoreStatus: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#666',
    marginTop: 10,
  },
  detailStats: {
    marginBottom: 30,
  },
  detailStatItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  detailStatLabel: {
    fontSize: 16,
    color: '#666',
  },
  detailStatValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  actionButtons: {
    gap: 15,
  },
  certificateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFD700',
    paddingVertical: 15,
    borderRadius: 10,
  },
  certificateButtonText: {
    marginLeft: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    borderRadius: 10,
  },
  shareButtonText: {
    marginLeft: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F44336',
    paddingVertical: 15,
    borderRadius: 10,
  },
  deleteButtonText: {
    marginLeft: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
});

export default AssessmentResultsScreen;

